import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { useIntl } from 'ha/i18n';
import { RequiredDocuments } from '@ha/api/v2/types/RequiredDocuments';
import { UserDocumentCategory } from '@ha/api/v2/types/UserDocument';

import { Grid, Typography, units } from '@hbf/dsl';
import {
  AccountingInvoiceBold,
  CommonFileTextAlternateBold,
  SingleNeutralIDCard4Bold,
} from '@ha/icons';

const useStyles = makeStyles()((theme: Theme) => ({
  documentsGroup: {
    margin: theme.spacing(2, 0, 0, 0),
  },
  icon: {
    marginRight: units.rem(0.5),
    position: 'relative',
    top: '1px',
    fontSize: units.rem(1.3),

    [theme.breakpoints.up('md')]: {
      top: '4px',
    },
  },
  'icon--secondaryDark': {
    color: theme.palette.secondary.dark,
  },
  'icon--grey': {
    color: theme.palette.grey[600],
  },
  subtitle: {
    marginTop: units.rem(0.5),
  },
  title: {
    [theme.breakpoints.down('lg')]: {
      fontSize: units.rem(1),
    },
  },
}));

interface Props {
  documentsList: Array<RequiredDocuments>;
  iconColour?: 'secondaryDark' | 'grey';
}

const RequiredDocumentsListing: React.FC<Props> = ({
  documentsList,
  iconColour = 'secondaryDark',
}) => {
  const { T } = useIntl();
  const { classes, cx } = useStyles();

  const iconStyle = cx(classes.icon, classes[`icon--${iconColour}`]);
  const CATEGORIES_CONTENT = {
    [UserDocumentCategory.identification]: {
      title: T('ldp.required_docs.id_visa'),
      description: T('ldp.required_docs.id_visa.tooltip'),
      icon: <SingleNeutralIDCard4Bold className={iconStyle} />,
    },
    [UserDocumentCategory.proofOfIncome]: {
      title: T('ldp.required_docs.income'),
      description: T('ldp.required_docs.income.tooltip'),
      icon: <AccountingInvoiceBold className={iconStyle} />,
    },
    [UserDocumentCategory.proofOfOccupation]: {
      title: T('ldp.required_docs.occupation'),
      description: T('ldp.required_docs.occupation.tooltip'),
      icon: <CommonFileTextAlternateBold className={iconStyle} />,
    },
  };

  return (
    <Grid container>
      {documentsList.map(document =>
        CATEGORIES_CONTENT[document.category] ? (
          <Grid
            item
            xs={12}
            data-test-locator={`Required Documents - ${document.category}`}
          >
            <Grid container className={classes.documentsGroup} wrap="nowrap">
              <Grid item>{CATEGORIES_CONTENT[document.category].icon}</Grid>
              <Grid item>
                <Typography.H4 className={classes.title}>
                  {CATEGORIES_CONTENT[document.category].title}
                </Typography.H4>
                <Typography.Paragraph className={classes.subtitle}>
                  {CATEGORIES_CONTENT[document.category].description}
                </Typography.Paragraph>
              </Grid>
            </Grid>
          </Grid>
        ) : null,
      )}
    </Grid>
  );
};

export { RequiredDocumentsListing };
